import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrder, deleteOrder, getFerias } from '../Services/api';
import './MainScreen.css';
import UserWithContacts from '../Models/Models';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import TableViewIcon from '@mui/icons-material/TableView';
import ListViewIcon from '@mui/icons-material/ViewList';
import { IconButton, Checkbox, FormControlLabel, FormGroup, Button, Modal, Box } from '@mui/material';
import ExportToExcel from '../Components/ExportToExcel';

const getMachineImage = (model) => {
    switch (model) {
        case 'K6': return './res/svg/maquinas/K6.svg';
        case 'K62': return './res/svg/maquinas/K8.svg';
        case 'K8': return './res/svg/maquinas/K8.svg';
        case 'K9T': return './res/svg/maquinas/K9T.svg';
        case 'K9': return './res/svg/maquinas/K9.svg';
        case 'K10': return './res/svg/maquinas/K10.svg';
        case 'K10T': return './res/svg/maquinas/K10T.svg';
        default: return './res/svg/maquinas/K10.svg';
    }
};

const MainScreen = () => {
    const { t } = useTranslation();
    const [userWithOrders, setUserWithOrders] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const [filters, setFilters] = useState({
        cliente: '',
        direccion: '',
        codPostal: '',
        poblacion: '',
        provincia: '',
        pais: '',
        telefono: '',
        email: '',
        modelo: '',
        observaciones: '',
        condiciones: '',
        vendedorEmail: ''
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isTableView, setIsTableView] = useState(() => {
        const savedView = localStorage.getItem('isTableView');
        return savedView ? JSON.parse(savedView) : true;
    });

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('columnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            cliente: true,
            direccion: true,
            codPostal: true,
            poblacion: true,
            provincia: true,
            pais: true,
            telefono: true,
            email: true,
            modelo: true,
            observaciones: true,
            condiciones: true,
            vendedorEmail: true
        };
    });
    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('columnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {};
    });
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [ferias, setFerias] = useState([]);
    const [activeFeria, setActiveFeria] = useState('');
    const [activeFeriaName, setActiveFeriaName] = useState('');
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const thRefs = useRef([]);
    const tdRefs = useRef([]);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        try {
            // Obtener ferias y establecer la feria activa si no está definida
            const feriasResponse = await getFerias(token);
            setFerias(feriasResponse);
            const activeFeriaResponse = feriasResponse.find(feria => feria.activa);
            if (activeFeriaResponse && !activeFeria) {
                setActiveFeria(activeFeriaResponse.feriaId);
                setActiveFeriaName(activeFeriaResponse.nombre);
            }

            // Obtener contactos
            const response = await getOrder(token);
            const user = response.user;
            const contacts = response.contacts;

            const userWithContacts = new UserWithContacts(
                user.usuarioId,
                user.email,
                user.contraseña,
                user.createdAt,
                user.updatedAt,
                user.permisos,
                contacts
            );
            setUserWithOrders(userWithContacts);

            // Filtrar contactos por la feria activa
            if (activeFeriaResponse) {
                const contactosFiltrados = contacts.filter(contacto =>
                    String(contacto.feriaId) === String(activeFeriaResponse.feriaId)
                );
                setFilteredContacts(contactosFiltrados);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    useEffect(() => {
        fetchData();
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []); // Se ejecuta solo una vez al cargar el componente

    // Filtrar contactos cuando cambia la feria activa o los filtros
    useEffect(() => {
        if (userWithOrders && userWithOrders.contactos) {
            let contactosFiltrados = userWithOrders.contactos;

            // Filtrar por feria seleccionada
            if (activeFeria) {
                contactosFiltrados = contactosFiltrados.filter(contacto =>
                    String(contacto.feriaId) === String(activeFeria)
                );
            }

            // Aplicar filtros adicionales
            contactosFiltrados = contactosFiltrados.filter(contacto =>
                Object.keys(filters).every(key =>
                    String(contacto[key] || '').toLowerCase().includes(String(filters[key] || '').toLowerCase())
                )
            );

            setFilteredContacts(contactosFiltrados);
        }
    }, [activeFeria, filters, userWithOrders]); // Se ejecuta cuando cambian estos valores

    useEffect(() => {
        // Apply saved column widths
        Object.keys(columnWidths).forEach((key, index) => {
            if (thRefs.current[index]) {
                thRefs.current[index].style.width = columnWidths[key];
            }
            tdRefs.current.forEach(row => {
                if (row[index]) {
                    row[index].style.width = columnWidths[key];
                }
            });
        });
    }, [columnWidths, filteredContacts, isTableView]);

    const handleAddOrder = () => {
        navigate('/add-order', { state: { selectedFeria: activeFeria } });
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const handleCardClick = (contacto) => {
        setSelectedContact(contacto);
    };

    const handleCloseModal = () => {
        setSelectedContact(null);
    };

    const handleDeleteOrder = async () => {
        const token = localStorage.getItem('token');
        try {
            await deleteOrder(selectedContact.contactoId, token);
            setSelectedContact(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting order:', error);
        }
    };

    const handleEditOrder = () => {
        navigate(`/edit-order/${selectedContact.contactoId}`, { state: { contact: selectedContact } });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
    };

    const clearFilters = () => {
        setFilters({
            cliente: '',
            direccion: '',
            codPostal: '',
            poblacion: '',
            provincia: '',
            pais: '',
            telefono: '',
            email: '',
            modelo: '',
            observaciones: '',
            condiciones: '',
            vendedorEmail: ''
        });
    };
    const toggleView = () => {
        const newView = !isTableView;
        setIsTableView(newView);
        localStorage.setItem('isTableView', JSON.stringify(newView));
    };
    const handleColumnVisibilityChange = (event) => {
        const { name, checked } = event.target;
        const newColumnVisibility = { ...columnVisibility, [name]: checked };
        setColumnVisibility(newColumnVisibility);
        localStorage.setItem('columnVisibility', JSON.stringify(newColumnVisibility));
    };
    const toggleColumnModal = () => {
        setShowColumnModal(!showColumnModal);
    };
    const toggleMobileMenu = () => {
        setShowMobileMenu(!showMobileMenu);
    };

    const handleFeriaChange = (event) => {
        setActiveFeria(event.target.value);
    };

    const handleMouseDown = (e, index) => {
        const startX = e.clientX;
        const startWidth = thRefs.current[index].offsetWidth;

        const handleMouseMove = (e) => {
            const newWidth = Math.max(100, startWidth + (e.clientX - startX));
            thRefs.current[index].style.width = `${newWidth}px`;
            tdRefs.current.forEach(row => {
                if (row[index]) {
                    row[index].style.width = `${newWidth}px`;
                }
            });
        };

        const handleMouseUp = () => {
            const newColumnWidths = { ...columnWidths, [index]: thRefs.current[index].style.width };
            setColumnWidths(newColumnWidths);
            localStorage.setItem('columnWidths', JSON.stringify(newColumnWidths));
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleTouchStart = (e, index) => {
        let defaultWidth;
        const screenWidth = window.innerWidth;
        if (screenWidth <= 768) {
            defaultWidth = '60px';
        } else if (screenWidth <= 1024) {
            defaultWidth = '70px';
        } else if (screenWidth <= 1500) {
            defaultWidth = '80px';
        } else if (screenWidth <= 1920) {
            defaultWidth = '100px';
        } else {
            defaultWidth = '100px';
        }
        const startX = e.touches[0].clientX;
        const startWidth = thRefs.current[index].offsetWidth;

        const handleTouchMove = (e) => {
            const newWidth = Math.max(defaultWidth, startWidth + (e.touches[0].clientX - startX));
            thRefs.current[index].style.width = `${newWidth}px`;
            tdRefs.current.forEach(row => {
                if (row[index]) {
                    row[index].style.width = `${newWidth}px`;
                }
            });
        };

        const handleTouchEnd = () => {
            const newColumnWidths = { ...columnWidths, [index]: thRefs.current[index].style.width };
            setColumnWidths(newColumnWidths);
            localStorage.setItem('columnWidths', JSON.stringify(newColumnWidths));
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };

        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
    };

    const resetColumnSettings = () => {
        const defaultVisibility = {
            cliente: true,
            direccion: true,
            codPostal: true,
            poblacion: true,
            provincia: true,
            pais: true,
            telefono: true,
            email: true,
            modelo: true,
            observaciones: true,
            condiciones: true,
            vendedorEmail: true
        };
        setColumnVisibility(defaultVisibility);
        localStorage.setItem('columnVisibility', JSON.stringify(defaultVisibility));

        const defaultWidths = {};
        const screenWidth = window.innerWidth;

        let defaultWidth;
        if (screenWidth <= 768) {
            defaultWidth = '60px';
        } else if (screenWidth <= 1024) {
            defaultWidth = '70px';
        } else if (screenWidth <= 1500) {
            defaultWidth = '80px';
        } else if (screenWidth <= 1920) {
            defaultWidth = '100px';
        } else {
            defaultWidth = '100px';
        }

        thRefs.current.forEach((th, index) => {
            if (th) {
                th.style.width = defaultWidth;
                defaultWidths[index] = defaultWidth;
            }
        });
        tdRefs.current.forEach(row => {
            row.forEach((td, index) => {
                if (td) {
                    td.style.width = defaultWidth;
                }
            });
        });
        setColumnWidths(defaultWidths);
        localStorage.setItem('columnWidths', JSON.stringify(defaultWidths));
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });

        const sortedContacts = [...filteredContacts].sort((a, b) => {
            if (a[key] < b[key]) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (a[key] > b[key]) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        setFilteredContacts(sortedContacts);
    };

    return (
        <div className='main-screen'>
            <div className="container">
                <div className="Header">
                    <img src="./KeitoSalesLogoSinFondo.svg" alt="Logo Keito" className="logoKeito" />
                    <div className="header-buttons">
                        <select value={activeFeria} onChange={handleFeriaChange}>
                            {ferias.map((feria) => (
                                <option key={feria.feriaId} value={feria.feriaId}>
                                    {feria.nombre}
                                </option>
                            ))}
                        </select>
                        {isMobile ? (
                            <>
                                <IconButton onClick={toggleMobileMenu}>
                                    <MenuIcon />
                                </IconButton>
                                <Modal
                                    open={showMobileMenu}
                                    onClose={toggleMobileMenu}
                                    aria-labelledby="mobile-menu-title"
                                    aria-describedby="mobile-menu-description"
                                >
                                    <Box className="mobile-menu">
                                        <button className='close-button' onClick={toggleMobileMenu}>
                                            <img src='./res/svg/close_icon.svg'></img>
                                        </button>
                                        <button className='edit-order-button' onClick={handleAddOrder}>
                                            <img src="./res/svg/add_user.svg" alt="Logout" className="header-icon" />
                                            {t('addOrder')}
                                        </button>
                                        {userWithOrders?.permisos === 2 &&
                                            <ExportToExcel data={filteredContacts} fileName={`Contactos_${activeFeriaName}`} isMobile={true} />
                                        }
                                        <button className='edit-order-button' onClick={handleLogout}>
                                            <img src="./res/svg/exit_icon.svg" alt="Logout" className="header-icon" />
                                            {t('closeSession')}
                                        </button>
                                    </Box>
                                </Modal>
                            </>
                        ) : (
                            <>
                                <button className="edit-order-button" onClick={handleAddOrder}>
                                    <img src="./res/svg/add_user.svg" alt="Add Order" className="add-order-icon" />
                                </button>
                                {isTableView && (
                                    <>
                                        <Button className="edit-order-button" onClick={toggleColumnModal}>
                                            <img src="./res/svg/edit_icon.svg" alt="Logout" className="header-icon" />
                                        </Button>
                                        <Button className="edit-order-button" onClick={clearFilters}>
                                            <img src="./res/svg/clear_filters_icon.svg" alt="Logout" className="header-icon" />
                                        </Button>
                                    </>
                                )}
                                {userWithOrders?.permisos === 2 &&
                                    <ExportToExcel data={filteredContacts} fileName={`Contactos_${activeFeriaName}`} isMobile={false} />
                                }

                                <IconButton className="edit-order-button" onClick={toggleView}>
                                    {isTableView ? <ListViewIcon sx={{ color: '#1B448F', fontSize: '2.5rem !important', padding: '0px !important' }} /> : <TableViewIcon sx={{ color: '#1B448F', fontSize: '2.5rem !important', padding: '0px !important' }} />}
                                </IconButton>
                                <button className="header-button-right" onClick={handleLogout}>
                                    <img src="./res/svg/exit_icon.svg" alt="Logout" className="header-icon" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <Modal
                    open={showColumnModal}
                    onClose={toggleColumnModal}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box className="column-modal">
                        <FormGroup>
                            {Object.keys(columnVisibility).map((column, index) => (
                                <FormControlLabel
                                    key={column}
                                    control={
                                        <Checkbox
                                            checked={columnVisibility[column]}
                                            onChange={handleColumnVisibilityChange}
                                            name={column}
                                        />
                                    }
                                    label={t(column)}
                                />
                            ))}
                        </FormGroup>
                        <Button className="ok-button" sx={{ backgroundColor: '#ff00008f !important', marginBottom: '10px' }} onClick={resetColumnSettings}>{t('Reset')}</Button>
                        <Button className="ok-button" onClick={toggleColumnModal}>{t('ok')}</Button>

                    </Box>
                </Modal>
                <div className="Content">
                    {isMobile || !isTableView ? (
                        <div className="order-list">
                            {filteredContacts && filteredContacts.length === 0 && (
                                <p>{t('noOrders')}</p>
                            )}
                            {filteredContacts && filteredContacts.map((pedido) => (
                                <div key={pedido.pedidoId} className="order-card" onClick={() => handleCardClick(pedido)}>
                                    <div className="order-details">
                                        {pedido.cliente && <h2>{pedido.cliente}</h2>}
                                        {pedido.pedidoId && <p>ID: {pedido.pedidoId}</p>}
                                        {pedido.fecha && <p>{new Date(pedido.fecha).toLocaleDateString()}</p>}
                                        {pedido.direccion && <p>{pedido.direccion}, {pedido.poblacion}, {pedido.provincia}, {pedido.pais}</p>}
                                        {pedido.telefono && <p>{pedido.telefono} - {pedido.email}</p>}
                                        {pedido.modelo && <p>{pedido.modelo}</p>}
                                        {pedido.observaciones && <p>{pedido.observaciones}</p>}
                                        {pedido.condiciones && <p>{pedido.condiciones}</p>}
                                        {pedido.vendedorEmail && <p>{pedido.vendedorEmail}</p>}
                                    </div>
                                    {pedido.modelo && <img src={getMachineImage(pedido.modelo)} alt={pedido.modelo} className="machine-image" />}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <table className="contact-table" ref={tableRef}>
                            <thead>
                                <tr>
                                    {columnVisibility.cliente && <th ref={el => thRefs.current[0] = el} onMouseDown={(e) => handleMouseDown(e, 0)} onTouchStart={(e) => handleTouchStart(e, 0)}> <button className="sort-button" onClick={() => handleSort('cliente')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('cliente')}</th>}
                                    {columnVisibility.direccion && <th ref={el => thRefs.current[1] = el} onMouseDown={(e) => handleMouseDown(e, 1)} onTouchStart={(e) => handleTouchStart(e, 1)}> <button className="sort-button" onClick={() => handleSort('direccion')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('direccion')}</th>}
                                    {columnVisibility.codPostal && <th ref={el => thRefs.current[2] = el} onMouseDown={(e) => handleMouseDown(e, 2)} onTouchStart={(e) => handleTouchStart(e, 2)}> <button className="sort-button" onClick={() => handleSort('codPostal')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('codPostal')}</th>}
                                    {columnVisibility.poblacion && <th ref={el => thRefs.current[3] = el} onMouseDown={(e) => handleMouseDown(e, 3)} onTouchStart={(e) => handleTouchStart(e, 3)}> <button className="sort-button" onClick={() => handleSort('poblacion')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('poblacion')}</th>}
                                    {columnVisibility.provincia && <th ref={el => thRefs.current[4] = el} onMouseDown={(e) => handleMouseDown(e, 4)} onTouchStart={(e) => handleTouchStart(e, 4)}> <button className="sort-button" onClick={() => handleSort('provincia')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('provincia')}</th>}
                                    {columnVisibility.pais && <th ref={el => thRefs.current[5] = el} onMouseDown={(e) => handleMouseDown(e, 5)} onTouchStart={(e) => handleTouchStart(e, 5)}> <button className="sort-button" onClick={() => handleSort('pais')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('pais')}</th>}
                                    {columnVisibility.telefono && <th ref={el => thRefs.current[6] = el} onMouseDown={(e) => handleMouseDown(e, 6)} onTouchStart={(e) => handleTouchStart(e, 6)}> <button className="sort-button" onClick={() => handleSort('telefono')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('telefono')}</th>}
                                    {columnVisibility.email && <th ref={el => thRefs.current[7] = el} onMouseDown={(e) => handleMouseDown(e, 7)} onTouchStart={(e) => handleTouchStart(e, 7)}><button className="sort-button" onClick={() => handleSort('email')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('email')} </th>}
                                    {columnVisibility.modelo && <th ref={el => thRefs.current[8] = el} onMouseDown={(e) => handleMouseDown(e, 8)} onTouchStart={(e) => handleTouchStart(e, 8)}><button className="sort-button" onClick={() => handleSort('modelo')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('modelo')} </th>}
                                    {columnVisibility.observaciones && <th ref={el => thRefs.current[9] = el} onMouseDown={(e) => handleMouseDown(e, 9)} onTouchStart={(e) => handleTouchStart(e, 9)}><button className="sort-button" onClick={() => handleSort('observaciones')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('observaciones')} </th>}
                                    {columnVisibility.condiciones && <th ref={el => thRefs.current[10] = el} onMouseDown={(e) => handleMouseDown(e, 10)} onTouchStart={(e) => handleTouchStart(e, 10)}> <button className="sort-button" onClick={() => handleSort('condiciones')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('condiciones')}</th>}
                                    {columnVisibility.vendedorEmail && <th ref={el => thRefs.current[11] = el} onMouseDown={(e) => handleMouseDown(e, 11)} onTouchStart={(e) => handleTouchStart(e, 11)}><button className="sort-button" onClick={() => handleSort('vendedorEmail')}><img src="./res/svg/sort_column.svg" alt="Logout" className="header-icon" /></button>{t('vendedorEmail')} </th>}

                                </tr>
                                <tr>
                                    {columnVisibility.cliente && <th><input type="text" name="cliente" value={filters.cliente} onChange={handleFilterChange} placeholder={t('cliente')} /></th>}
                                    {columnVisibility.direccion && <th><input type="text" name="direccion" value={filters.direccion} onChange={handleFilterChange} placeholder={t('direccion')} /></th>}
                                    {columnVisibility.codPostal && <th><input type="text" name="codPostal" value={filters.codPostal} onChange={handleFilterChange} placeholder={t('codPostal')} /></th>}
                                    {columnVisibility.poblacion && <th><input type="text" name="poblacion" value={filters.poblacion} onChange={handleFilterChange} placeholder={t('poblacion')} /></th>}
                                    {columnVisibility.provincia && <th><input type="text" name="provincia" value={filters.provincia} onChange={handleFilterChange} placeholder={t('provincia')} /></th>}
                                    {columnVisibility.pais && <th><input type="text" name="pais" value={filters.pais} onChange={handleFilterChange} placeholder={t('pais')} /></th>}
                                    {columnVisibility.telefono && <th><input type="text" name="telefono" value={filters.telefono} onChange={handleFilterChange} placeholder={t('telefono')} /></th>}
                                    {columnVisibility.email && <th><input type="text" name="email" value={filters.email} onChange={handleFilterChange} placeholder={t('email')} /></th>}
                                    {columnVisibility.modelo && <th><input type="text" name="modelo" value={filters.modelo} onChange={handleFilterChange} placeholder={t('modelo')} /></th>}
                                    {columnVisibility.observaciones && <th><input type="text" name="observaciones" value={filters.observaciones} onChange={handleFilterChange} placeholder={t('observaciones')} /></th>}
                                    {columnVisibility.condiciones && <th><input type="text" name="condiciones" value={filters.condiciones} onChange={handleFilterChange} placeholder={t('condiciones')} /></th>}
                                    {columnVisibility.vendedorEmail && <th><input type="text" name="vendedorEmail" value={filters.vendedorEmail} onChange={handleFilterChange} placeholder={t('vendedorEmail')} /></th>}

                                </tr>
                            </thead>
                            <tbody>
                                {filteredContacts && filteredContacts.length === 0 && (
                                    <tr>
                                        <td colSpan="13">{t('noOrders')}</td>
                                    </tr>
                                )}
                                {filteredContacts && filteredContacts.map((contacto, rowIndex) => (
                                    <tr key={contacto.contactoId} onClick={() => handleCardClick(contacto)}>
                                        <>
                                            {columnVisibility.cliente && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][0] = el; }}>{contacto.cliente}</td>}
                                            {columnVisibility.direccion && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][1] = el; }}>{contacto.direccion}</td>}
                                            {columnVisibility.codPostal && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][2] = el; }}>{contacto.codPostal}</td>}
                                            {columnVisibility.poblacion && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][3] = el; }}>{contacto.poblacion}</td>}
                                            {columnVisibility.provincia && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][4] = el; }}>{contacto.provincia}</td>}
                                            {columnVisibility.pais && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][5] = el; }}>{contacto.pais}</td>}
                                            {columnVisibility.telefono && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][6] = el; }}>{contacto.telefono}</td>}
                                            {columnVisibility.email && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][7] = el; }}>{contacto.email}</td>}
                                            {columnVisibility.modelo && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][8] = el; }}>{contacto.modelo}</td>}
                                            {columnVisibility.observaciones && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][9] = el; }}>{contacto.observaciones}</td>}
                                            {columnVisibility.condiciones && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][10] = el; }}>{contacto.condiciones}</td>}
                                            {columnVisibility.vendedorEmail && <td ref={el => { tdRefs.current[rowIndex] = tdRefs.current[rowIndex] || []; tdRefs.current[rowIndex][11] = el; }}>{contacto.vendedorEmail}</td>}

                                        </>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {selectedContact && (
                <div className="modal">
                    <div className="modal-content">
                        <button className='close-button' onClick={handleCloseModal}>
                            <img src='./res/svg/close_icon.svg'></img>
                        </button>
                        <h2>{selectedContact.cliente}</h2>
                        <p>{selectedContact.direccion}, {selectedContact.poblacion}, {selectedContact.provincia}, {selectedContact.pais}</p>
                        <p>{selectedContact.telefono} - {selectedContact.email}</p>
                        <p>{selectedContact.modelo}</p>
                        <p>{selectedContact.observaciones}</p>
                        <p>{selectedContact.condiciones}</p>
                        <p>{selectedContact.vendedorEmail}</p>
                        <button className="edit-button" onClick={handleEditOrder}>{t('editOrderButton')}</button>
                        {userWithOrders?.permisos !== 1 && (
                            <button className="delete-button" onClick={handleDeleteOrder}>{t('deleteOrderButton')}</button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MainScreen;
